import { useState } from 'react';
import {
  ChevronDownBigIcon,
  PencilFullSmallIcon,
  QuestionnaireDotLine,
  Sparkles
} from '../../../icons/SvgIcons';
import { FollowUpAnswer } from '../../../types/AIClassification.types';
import { WhiteButton } from '../../ui/Button/Buttons';
import { Divider } from '../../ui/Divider/Divider';
import { QuestionaireComponent } from '../../ui/Questionaire/QuestionaireComponent';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { setIsEditClassificationPathEnabled } from '../../../store/slices/aiPoweredSlice';

type ClassificationPathProps = {
  description: string;
  descriptionQuality: string;
  followUpAnswers: FollowUpAnswer[];
  loadingNextQuestion: boolean;
  rerunLoading: boolean;
  editingIndex: number | null;
  isItemDisabled: (itemIndex: number) => boolean;
  onDescriptionUpdate: (newDescription: string) => void;
  onAnswerUpdate: (index: number, newAnswer: string) => void;
  onEditStart: (
    itemIndex: number,
    answerIndex?: number | null,
    showFooterClarification?: boolean
  ) => void;
  onEditEnd: () => void;
};

const ClassificationPath = ({
  description,
  descriptionQuality,
  followUpAnswers,
  loadingNextQuestion,
  rerunLoading,
  editingIndex,
  isItemDisabled,
  onDescriptionUpdate,
  onAnswerUpdate,
  onEditStart,
  onEditEnd
}: ClassificationPathProps) => {
  const dispatch = useAppDispatch();
  const { isEditClassificationPathEnabled } = useAppSelector(
    (state) => state.aiPowered
  );
  const [height, setHeight] = useState(0);
  const [expandClassificationPath, setExpandClassificationPath] =
    useState(false);

  const shouldShowFooterClarification = (answer: FollowUpAnswer) =>
    Boolean(
      answer.options?.length &&
        answer.options.some((option) => option.trim() === answer.answer.trim())
    );

  const handleEditClassificationPath = () => {
    const nextValue = !isEditClassificationPathEnabled;
    dispatch(setIsEditClassificationPathEnabled(nextValue));
    setExpandClassificationPath(nextValue);

    if (!nextValue) {
      onEditEnd();
    }
  };

  return (
    <div className="relative flex flex-col gap-6 mb-6">
      <div className="flex justify-between items-center h-[38px]">
        <span className="h-full text-sm text-zinc-900 font-medium">
          Classification Path
        </span>
        <div className="flex gap-4">
          <WhiteButton
            onClick={handleEditClassificationPath}
            size="xs"
            icon={
              isEditClassificationPathEnabled ? undefined : (
                <PencilFullSmallIcon />
              )
            }
            className="h-[30px] text-gray-800"
          >
            {isEditClassificationPathEnabled
              ? 'Cancel Editing'
              : 'Edit Assessment'}
          </WhiteButton>
          <button
            onClick={() => setExpandClassificationPath((prev) => !prev)}
            className={`transition-transform duration-200 ease-in-out ${
              expandClassificationPath ? '' : 'rotate-180'
            }`}
          >
            {ChevronDownBigIcon()}
          </button>
        </div>
      </div>

      {expandClassificationPath && (
        <div className="relative">
          {!isEditClassificationPathEnabled && (
            <div
              className="absolute top-[33px] left-[32px] pointer-events-none w-[2px]"
              style={{ bottom: `${height}px` }}
            >
              <QuestionnaireDotLine className="h-full" />
            </div>
          )}

          <div className="flex flex-col gap-4">
            <QuestionaireComponent
              icon={<Sparkles fill="#4E4E56" />}
              mainText={descriptionQuality}
              description={description}
              descriptionTitle="AI System Information"
              isLoading={false}
              isEditable={
                !loadingNextQuestion && isEditClassificationPathEnabled
              }
              isGrayedOut={!isEditClassificationPathEnabled}
              isDisabled={rerunLoading || isItemDisabled(0)}
              onDescriptionUpdate={onDescriptionUpdate}
              onEditStart={() => onEditStart(0)}
              onEditEnd={onEditEnd}
            />

            {followUpAnswers.map((answer, index) => {
              const itemIndex = index + 1;

              return (
                <div
                  key={answer.question}
                  {...(editingIndex === itemIndex
                    ? { 'data-ai-active-question': 'true' }
                    : {})}
                >
                  <QuestionaireComponent
                    icon={<Sparkles fill="#4E4E56" />}
                    mainText={answer.question}
                    isLoading={false}
                    isEditable={
                      !loadingNextQuestion && isEditClassificationPathEnabled
                    }
                    isGrayedOut={!isEditClassificationPathEnabled}
                    isDisabled={isItemDisabled(itemIndex)}
                    isEditActive={editingIndex === itemIndex}
                    userAnswer={answer.answer}
                    answerOptions={answer.options}
                    onUserAnswerUpdate={(newAnswer) =>
                      onAnswerUpdate(index, newAnswer)
                    }
                    onEditStart={() =>
                      onEditStart(
                        itemIndex,
                        index,
                        shouldShowFooterClarification(answer)
                      )
                    }
                    onEditEnd={onEditEnd}
                    onHeightChange={(height) => setHeight(height)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Divider />
    </div>
  );
};

export default ClassificationPath;
